/**
 * <coming-soon-mention>
 * Shows the "coming soon" mention only when the currently selected variant is
 * out of stock. The mention is always present in the DOM; this component is the
 * single source of truth for its visibility, so it stays correct regardless of
 * page caching or whether the theme re-renders the product section.
 *
 * Initial state is derived from the selected variant (URL `?variant`, then the
 * server-rendered fallback) looked up in an embedded availability map. On every
 * `variant:change` event it re-evaluates from the event's variant.
 */
if (!customElements.get('coming-soon-mention')) {
  class ComingSoonMention extends HTMLElement {
    static SELECTORS = {
      label: '.product__coming-soon',
      data: 'script[type="application/json"]'
    };

    static EVENTS = { variantChange: 'variant:change' };
    static HIDDEN_ATTRIBUTE = 'hidden';

    connectedCallback () {
      this._abort = new AbortController();
      this._label = this.querySelector(ComingSoonMention.SELECTORS.label);
      this._availability = this._parseAvailability();

      if (!this._label) return;

      this._toggle(this._isSelectedVariantAvailable());

      document.addEventListener(
        ComingSoonMention.EVENTS.variantChange,
        (event) => this._onVariantChange(event),
        { signal: this._abort.signal }
      );
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    _parseAvailability () {
      const script = this.querySelector(ComingSoonMention.SELECTORS.data);

      try {
        return script ? JSON.parse(script.textContent) : {};
      } catch (error) {
        return {};
      }
    }

    _selectedVariantId () {
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      return fromUrl || this.dataset.selectedVariant || null;
    }

    _isSelectedVariantAvailable () {
      const id = this._selectedVariantId();
      // Unknown variant -> treat as unavailable so the mention shows (safer for a coming-soon product).
      return id != null && this._availability[id] === true;
    }

    _onVariantChange (event) {
      const variant = event.detail?.variant;
      if (variant == null) return;

      this._toggle(variant.available === true);
    }

    // Hide the mention when the variant is available, show it when it is not.
    _toggle (available) {
      this._label.toggleAttribute(ComingSoonMention.HIDDEN_ATTRIBUTE, available);
    }
  }

  customElements.define('coming-soon-mention', ComingSoonMention);
}
